import { createBoxShadow, DomDocumentHelper, DomWindowHelper, parseBoxShadow } from "survey-core";
import { getRGBaColor } from "./color-utils";

export function roundTo2Decimals(number: number): number {
  return Math.round(number * 100) / 100;
}

/**
 * Applies CSS variables on a temporary `.sd-theme-root` element and replaces them with computed values
 * (resolves `calc()`, normalizes border-effect box-shadows, and converts color variables to RGBA strings).
 */
export function calculateThemeVariables(
  cssVariables: { [key: string]: string } | undefined | null,
  additionalCssVariables: string[] = [],
  rootElement?: HTMLElement
): { [key: string]: string } | undefined | null {
  if (cssVariables == null) {
    return cssVariables as undefined | null;
  }
  let themeCopyCssVariables = JSON.parse(JSON.stringify(cssVariables)) as { [key: string]: string };

  const body = rootElement || DomDocumentHelper.getBody();
  const window = DomWindowHelper.getWindow();
  if (themeCopyCssVariables && !!window && !!body) {
    const div = DomDocumentHelper.createElement("div");
    for (const key of Object.keys(themeCopyCssVariables)) {
      div.style.setProperty(key, themeCopyCssVariables[key] as string);
    }
    div.classList.add("sd-theme-root");
    body.appendChild(div);

    const computed = window.getComputedStyle(div);

    const newCssVariables: { [key: string]: string } = {};
    const calcProxySizeProperty = "width";
    const calcProxyBoxShadowProperty = "box-shadow";
    for (const key of [...Object.keys(themeCopyCssVariables), ...additionalCssVariables]) {
      const sourceValue = themeCopyCssVariables[key];
      let value = computed.getPropertyValue(key);
      if (typeof value === "string" && value.trim() === "") {
        if (sourceValue !== undefined) {
          newCssVariables[key] = sourceValue;
        }
        continue;
      }
      if (typeof value === "string" && value.indexOf("calc(") === 0) {
        div.style.setProperty(calcProxySizeProperty, value);
        value = computed.getPropertyValue(calcProxySizeProperty);
        div.style.removeProperty(calcProxySizeProperty);
      }
      if (key.indexOf("-border-effect-") !== -1) {
        div.style.setProperty(calcProxyBoxShadowProperty, value);
        value = createBoxShadow(parseBoxShadow(computed.getPropertyValue(calcProxyBoxShadowProperty)));
        div.style.removeProperty(calcProxyBoxShadowProperty);
      }
      if (key.indexOf("-color-") !== -1 && value !== "transparent") {
        value = getRGBaColor(value);
      }
      newCssVariables[key] = value;
    }
    themeCopyCssVariables = newCssVariables;

    body.removeChild(div);
  }
  return themeCopyCssVariables;
}

const endLineRegExp = new RegExp("(\\r\\n|\\n|\\r)", "gm");
export function clearNewLines(text: string) {
  return text.replace(endLineRegExp, "");
}

export function copyObject(dst: any, src: any) {
  for (let key in src) {
    let source = src[key];
    if (typeof source === "object") {
      source = {};
      copyObject(source, src[key]);
    }
    dst[key] = source;
  }
}
export function copyCssClasses(dest: any, source: any) {
  if (!source) return;
  if (typeof source === "string" || source instanceof String) {
    dest["root"] = source;
  } else {
    copyObject(dest, source);
  }
}

export function capitalize(str: string): string {
  if (!str) return str;
  str = str.replace(/[\s]+/g, " ");
  str = str.replace(/([\s]|^)(\S)/g, (_, p1, p2) => {
    return p1 + p2.toUpperCase();
  });
  return str;
}

export function notShortCircuitAnd(...args: Array<boolean>): boolean {
  return args.every((val) => val === true);
}

export const imageMimeTypes = "image/png, image/gif, image/jpeg, image/apng, image/avif, image/svg+xml, image/webp";

export function getAcceptedTypesByContentMode(contentMode: string) {
  if (["auto", "image"].indexOf(contentMode) > -1) {
    return imageMimeTypes;
  } else if (contentMode == "video") {
    return "video/*";
  } else {
    return "";
  }
}

export function trimEmptyFields(object: { [index: string]: any | undefined | null }): void {
  Object.keys(object).forEach(key => {
    if (object[key] === undefined || object[key] === null) {
      delete object[key];
    }
  });
}

export function assign(...inputs: Array<any>) {
  const objects = (inputs || []).filter(obj => !!obj);
  if (objects.length <= 1) {
    return;
  }
  if (objects.length == 2) {
    Object.keys(objects[1]).forEach(key => objects[0][key] = objects[1][key]);
    trimEmptyFields(inputs[0]);
    return;
  }
  for (let i = 1; i < objects.length; i++) {
    assign(objects[0], objects[i]);
  }
}

export function getOS(): "Mac OS" | "iOS" | "Windows" | "Android" | "Linux" | null {
  const window = DomWindowHelper.getWindow();
  if (!window) return null;
  const userAgent = window.navigator.userAgent,
    platform = (window.navigator as any)?.userAgentData?.platform || window.navigator.platform,
    macosPlatforms = ["macOS", "Macintosh", "MacIntel", "MacPPC", "Mac68K"],
    windowsPlatforms = ["Win32", "Win64", "Windows", "WinCE"],
    iosPlatforms = ["iPhone", "iPad", "iPod"];
  let os = null;

  if (macosPlatforms.indexOf(platform) !== -1) {
    os = "Mac OS";
  } else if (iosPlatforms.indexOf(platform) !== -1) {
    os = "iOS";
  } else if (windowsPlatforms.indexOf(platform) !== -1) {
    os = "Windows";
  } else if (/Android/.test(userAgent)) {
    os = "Android";
  } else if (/Linux/.test(platform)) {
    os = "Linux";
  }

  return os;
}

export function cssVariablesToString(cssVariables: { [key: string]: string } | null | undefined): string {
  if (!cssVariables) {
    return "";
  }
  return Object.keys(cssVariables)
    .map(key => `${key}: ${cssVariables[key]}`)
    .join("; ");
}

export function levenshteinDistance(str1: string, str2: string): number {
  if (!str1) return str2 ? str2.length : 0;
  if (!str2) return str1.length;
  if (str1 === str2) return 0;
  if (str1.length > str2.length) {
    [str1, str2] = [str2, str1];
  }
  const len1 = str1.length;
  const len2 = str2.length;
  let prevRow = [];
  let currRow = [];
  for (let j = 0; j <= len2; j++) {
    prevRow[j] = j;
  }
  for (let i = 1; i <= len1; i++) {
    currRow[0] = i;
    for (let j = 1; j <= len2; j++) {
      const cost = str1[i - 1] === str2[j - 1] ? 0 : 1;
      currRow[j] = Math.min(
        prevRow[j] + 1,
        currRow[j - 1] + 1,
        prevRow[j - 1] + cost
      );
    } [prevRow, currRow] = [currRow, prevRow];
  }
  return prevRow[len2];
}